<script setup lang="ts">
import { reactive, ref, watchEffect } from 'vue'
import { usePageLoaded } from '../composables/usePageLoaded'
import { useSectionReveal } from '../composables/useSectionReveal'
import type { LocaleCode, LocalizedCopy, StoreLocation } from '../types/site'
import { localized } from '../utils/localized'
import SectionTitle from './SectionTitle.vue'

const props = defineProps<{
  stores: StoreLocation[]
  copy: LocalizedCopy
  locale: LocaleCode
  fallbackLocale: LocaleCode
  watermarkImage: string
}>()

const sectionRoot = ref<HTMLElement | null>(null)
useSectionReveal(sectionRoot)

// 门店大图在首屏以下，等页面 load 之后再请求（原因见 usePageLoaded）
const pageLoaded = usePageLoaded()

const selected = reactive<Record<string, string>>({})

watchEffect(() => {
  for (const store of props.stores) {
    if (!selected[store.id]) selected[store.id] = store.mainImage
  }
})
</script>

<template>
  <section id="stores" ref="sectionRoot" class="stores-section page-section">
    <SectionTitle
      eyebrow="OFFLINE STORE"
      :title="copy.stores.title"
      :watermark="watermarkImage"
    />
    <p class="section-intro">{{ copy.stores.intro }}</p>

    <article v-for="store in stores" :key="store.id" class="store-card">
      <div class="store-card__main-wrap">
        <img
          v-if="pageLoaded"
          class="store-card__main"
          :src="selected[store.id]"
          :alt="localized(store.name, locale, fallbackLocale)"
          loading="lazy"
          decoding="async"
        />
      </div>

      <div class="thumbnail-marquee" aria-label="Store gallery">
        <div class="thumbnail-marquee__track">
          <button
            v-for="(image, index) in [...store.thumbnails, ...store.thumbnails]"
            :key="`${store.id}-${index}`"
            type="button"
            :class="{ active: selected[store.id] === image }"
            :data-track="`store-thumb:${store.id}`"
            @click="selected[store.id] = image"
          >
            <img v-if="pageLoaded" :src="image" alt="" loading="lazy" decoding="async" />
          </button>
        </div>
      </div>

      <div class="store-card__details">
        <span class="store-card__pin" aria-hidden="true">♥</span>
        <strong>{{ localized(store.name, locale, fallbackLocale) }}</strong>
        <span>{{ localized(store.address, locale, fallbackLocale) }}</span>
      </div>
    </article>
  </section>
</template>
